import * as React from "react";
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import JobForm from "./JobFormContext";
import { LexicalRichTextareaEditor } from "../../../forms/components/LexicalRichTextareaEditor";
import DideWriterModal, {
  DideWriterApplyPayload,
} from "../dide-writer/DideWriterModal";
import UpsellCard from "../dide-writer/UpsellCard";
import type { BenefitKey, JobFields } from "../dide-writer/types";

const DIDE_BENEFIT_KEYS: BenefitKey[] = ['medical', 'dental', 'vision', '401k', 'pto', 'bonuses'];

const numberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

// The job form values the writer uses. Sent on every request because the
// job may not be saved yet; `job_id` lets the server fill in the rest.
const dideJobFields = (jobState): JobFields => ({
  job_id: jobState.id ? Number(jobState.id) : undefined,
  job_title: jobState.job_title || undefined,
  compensation_type: jobState.compensation_type || undefined,
  compensation: numberOrNull(jobState.compensation),
  min_compensation: numberOrNull(jobState.min_compensation),
  exact_pay: Boolean(jobState.exact_pay),
  doe: Boolean(jobState.doe),
  start_date: jobState.start_date || null,
  temp_date: jobState.temp_date || null,
  temp_start: jobState.temp_start || null,
  temp_end: jobState.temp_end || null,
  benefits: DIDE_BENEFIT_KEYS.filter((key) => jobState[`benefit_${key}`] === true),
  confidential: Boolean(jobState.confidential),
  description: jobState.description || undefined,
});

// Helper function to sanitize links from HTML content
const sanitizeLinks = (html: string): string => {
  // If running in a browser environment
  if (typeof document !== 'undefined') {
    const div = document.createElement('div');
    div.innerHTML = html;

    // Find all anchor tags
    const links = div.querySelectorAll('a');

    // Replace each link with its text content
    links.forEach(link => {
      const textNode = document.createTextNode(link.textContent || '');
      if (link.parentNode) {
        link.parentNode.replaceChild(textNode, link);
      }
    });

    // Return the sanitized HTML
    return div.innerHTML;
  }

  // Fallback for non-browser environments or SSR
  return html.replace(/<a\b[^>]*>(.*?)<\/a>/gi, '$1');
};

export default function Description({
  job_types,
  practices = [],
  tabIndexFunc,
  validation,
  form_submit,
}) {
  const [jobState, setJobState] = React.useContext(JobForm);
  const [htmlContent, setHtmlContent] = React.useState(jobState.description || '');
  const [errorMessage, setErrorMessage] = React.useState(false);
  const [editorKey, setEditorKey] = React.useState(0); // Add this to force remount
  const [showDideModal, setShowDideModal] = React.useState(false);
  const [dideUpsellDismissed, setDideUpsellDismissed] = React.useState(false);

  const selectedPractice = practices.find(
    (p) => Number(p.id) === Number(jobState.practice_id)
  );
  const selectedJobType = job_types.find(
    (jt) => Number(jt.id) === Number(jobState.job_type_id)
  );
  const dideEnabled = Boolean(selectedPractice?.dide_writer_enabled);
  const canOpenDide = dideEnabled && Boolean(selectedJobType);

  const applyDideDraft = React.useCallback((payload: DideWriterApplyPayload) => {
    setJobState((prev) => ({
      ...prev,
      ai_job_description_draft_id: payload.draft_id,
      ai_applied_fields: payload.applied_fields,
      ...payload.benefitColumns,
      ...(payload.title ? { job_title: payload.title } : {}),
      ...(payload.experience_required ? { experience_required: payload.experience_required } : {}),
    }));
    if (!payload.description && !payload.description_html) return;
    try {
      // Prefer the server's sanitized HTML; fall back to rendering markdown.
      let raw = payload.description_html || '';
      if (!raw && payload.description) {
        const result = marked(payload.description, { breaks: true, gfm: true });
        raw = result instanceof Promise ? '' : result;
      }
      // AI output is untrusted (prompt-injection surface); sanitize before
      // it reaches the rich-text editor or any dangerouslySetInnerHTML.
      const html = sanitizeLinks(DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } }));
      setHtmlContent(html);
      setEditorKey((k) => k + 1);
      setJobState((prev) => ({ ...prev, description: html }));
    } catch (err) {
      // Swallow: failure to render the AI draft must never block the form.
      // Rollbar catches the exception via the global error boundary.
      if (typeof window !== 'undefined' && (window as { Rollbar?: { error: (e: unknown) => void } }).Rollbar) {
        (window as { Rollbar?: { error: (e: unknown) => void } }).Rollbar?.error(err);
      }
    }
  }, [setJobState]);

  const templateEditor = job_types.find(
    item => item.id === Number(jobState.job_type_id)
  )?.description_template || '';

  React.useEffect(() => {
    // Process standard markdown first, then handle custom underline syntax
    marked.use({
      extensions: [{
        name: 'underline',
        level: 'inline',
        start(src) { return src.match(/\+\+/)?.index; },
        tokenizer(src) {
          const rule = /^\+\+([^+]+)\+\+/;
          const match = rule.exec(src);
          if (match) {
            // Parse the content inside ++ for other markdown
            const innerContent = marked.parseInline(match[1]);
            return {
              type: 'underline',
              raw: match[0],
              text: innerContent,
              tokens: []
            };
          }
          return undefined;
        },
        renderer(token) {
          return `<u>${token.text}</u>`;
        }
      }]
    });
  }, []);

  const handleEditorChange = (newHtmlContent: string) => {
    // Sanitize links from the HTML content
    const sanitizedContent = sanitizeLinks(newHtmlContent);

    setHtmlContent(sanitizedContent);
    setJobState({
      ...jobState,
      description: sanitizedContent
    });
  };

  const convertMarkdownToHtml = (markdown: string) => {
    try {
      marked.setOptions({
        breaks: true,
        gfm: true
      });
      // Handle both synchronous and asynchronous marked versions
      const result = marked(markdown);
      if (result instanceof Promise) {
        // If it's a promise, return a placeholder and update later
        setTimeout(() => {
          result.then(html => {
            // Force a re-render when the promise resolves
            setJobState({ ...jobState });
          });
        }, 0);
        return { __html: "Loading..." };
      }
      return { __html: result };
    } catch (error) {
      console.error('Markdown parsing error:', error);
      return { __html: markdown };
    }
  };

  const headlineHandler = e => {
    setJobState({ ...jobState, job_title: e.target.value });
  };

  const submitJobForm = (form_submit, options = {}) => {
    if (htmlContent.length > 33 && !validation) {
      form_submit(options);
    } else {
      setErrorMessage(true);
      console.error("Not all fields are filled out");
    }
  };

  const setDescriptionTemplate = async markdown => {
    try {
      // Handle both synchronous and asynchronous marked versions
      const result = marked(markdown, { breaks: true });
      const html = result instanceof Promise ? await result : result;

      // Sanitize links from HTML before setting it
      const sanitizedHtml = sanitizeLinks(html);
      handleEditorChange(sanitizedHtml);
      setEditorKey(prev => prev + 1); // Force editor remount
      setPreviewTemplate(false);
    } catch (error) {
      console.error('Error processing template:', error);
    }
  };

  const [previewTemplate, setPreviewTemplate] = React.useState(false);

  return (
    <div className="job-builder-step job-builder-step--description">
      <div className="job-builder-step__summary">
        <div>
          <strong>Write for the candidate</strong>
          <p>Highlight the day-to-day work, your team, and what makes this opportunity worth exploring.</p>
        </div>
        <i className="fa fa-file-alt" aria-hidden="true"></i>
      </div>
      <div className="form-group py-2">
        <h4 className="font-weight-bold">
          Job Headline (Optional)
        </h4>
        <p className="job-builder-field-help">Use a short hook, such as a schedule perk or team benefit.</p>
        <input
          className="form-control"
          type="text"
          placeholder="Example: Join a patient-first team with Fridays off"
          onChange={e => headlineHandler(e)}
          value={jobState.job_title ?? ""}
        ></input>
      </div>
      <div className="job-builder-template">
        {!previewTemplate && (
          <button type="button" className="job-builder-inline-action" onClick={() => setPreviewTemplate(true)}>
            <i className="fa fa-magic" aria-hidden="true"></i>
            Use a Template
          </button>
        )}
        {previewTemplate && !jobState.job_type_id && (
          <p className="text-danger ps-0 ms-0">Missing job type from general info.</p>
        )}
        {jobState.job_type_id && previewTemplate && (
          <div className="job-builder-template__preview">
            <div dangerouslySetInnerHTML={convertMarkdownToHtml(templateEditor)} />
            <div className="job-builder-template__actions">
              <button type="button" className="job-builder-button job-builder-button--secondary" onClick={() => setPreviewTemplate(false)}>
                Close
              </button>
              <button
                type="button"
                className="job-builder-button job-builder-button--primary"
                onClick={() => setDescriptionTemplate(templateEditor)}
              >
                Use this Template
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="form-group py-2">
        <div className="d-flex align-items-center justify-content-between">
          <h4 className="font-weight-bold mb-0">Job Description</h4>
          {canOpenDide && (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => setShowDideModal(true)}
              data-testid="open-dide-writer"
            >
              Write it for me
            </button>
          )}
        </div>
        <p className="job-builder-field-help">Include responsibilities, schedule expectations, and what success looks like.</p>
        {!dideEnabled && !dideUpsellDismissed && selectedPractice && (
          <UpsellCard onDismiss={() => setDideUpsellDismissed(true)} />
        )}
        <LexicalRichTextareaEditor
          key={editorKey} // Add this to force remount
          editorState={htmlContent}
          onChangeDescription={handleEditorChange}
        />
        {htmlContent.length < 34 && (
          <p className="job-builder-field-help job-builder-field-help--requirement">
            Add at least 34 characters before submitting.
          </p>
        )}
      </div>
      {canOpenDide && (
        <DideWriterModal
          show={showDideModal}
          onHide={() => setShowDideModal(false)}
          onApply={applyDideDraft}
          practiceId={Number(jobState.practice_id)}
          jobTypeId={jobState.job_type_id}
          positionType={jobState.position_type}
          skillIds={Array.isArray(jobState.skill_ids) ? jobState.skill_ids : []}
          yearsExperience={jobState.years_experience}
          jobFields={dideJobFields(jobState)}
        />
      )}
      {/* Submit refuses on two different grounds; say which one, next to the
          button that just did nothing. Previously the only message was tied to
          the description length, so a blocked submit from an earlier step gave
          no feedback at all. */}
      {errorMessage && (htmlContent.length < 34 || validation) && (
        <p className="job-builder-validation" role="alert">
          {htmlContent.length < 34
            ? "Add at least 34 characters to the job description before submitting."
            : "Some required details are still missing. Check the earlier steps for highlighted fields."}
        </p>
      )}
      <div className={jobState.published ? 'job-builder-actions' : 'job-builder-actions job-builder-actions--draft'}>
        <button
          type="button"
          className="job-builder-button job-builder-button--secondary"
          onClick={() => {
            tabIndexFunc(2)
          }}
        >
          <i className="fa fa-arrow-left" aria-hidden="true"></i>
          Back
        </button>
        {jobState.published ? (
          <button
            type="button"
            className="job-builder-button job-builder-button--primary job-builder-button--submit"
            onClick={() => {
              setErrorMessage(true);
              submitJobForm(form_submit);
            }}
          >
            <i className="fa fa-check" aria-hidden="true"></i>
            Save changes
          </button>
        ) : (
          <div className="job-builder-actions__group">
            {/* Unpublished job: saving keeps it a draft; publishing is one click,
                not a second step on the preview page (people left it there). */}
            <button
              type="button"
              className="job-builder-button job-builder-button--secondary job-builder-button--submit"
              onClick={() => {
                setErrorMessage(true);
                submitJobForm(form_submit);
              }}
            >
              <i className="far fa-save" aria-hidden="true"></i>
              Save draft
            </button>
            <button
              type="button"
              className="job-builder-button job-builder-button--primary job-builder-button--submit"
              onClick={() => {
                setErrorMessage(true);
                submitJobForm(form_submit, { publish: true });
              }}
            >
              <i className="far fa-newspaper" aria-hidden="true"></i>
              Publish job
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
